import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getTitle } from '../../../../../common/elements/utils/elementPropertyUtils';
import { makeSelectBoardLinkLists, SEARCH_SIZE } from './boardLinkSelector';
import { BoardLinkSearchItem } from './boardLinkTypes';

export type UseBoardLinkContentResult = {
    searchQuery?: string;
    setSearchQuery: (query: string) => void;
    gridSize: number;
    items: BoardLinkSearchItem[];
    isLoading: boolean;
    hasError: boolean;
    isEmpty: boolean;
};

const getNormalisedTitle = (title?: string): string => title?.trim().toLowerCase() || '';

const sortMatchingBoardItems =
    (normalisedQuery: string) =>
    (itemA: BoardLinkSearchItem, itemB: BoardLinkSearchItem): number => {
        const titleA = getNormalisedTitle(itemA.title);
        const titleB = getNormalisedTitle(itemB.title);

        const startsWithA = titleA.startsWith(normalisedQuery);
        const startsWithB = titleB.startsWith(normalisedQuery);

        if (startsWithA !== startsWithB) {
            return startsWithA ? -1 : 1;
        }

        return titleA.indexOf(normalisedQuery) - titleB.indexOf(normalisedQuery);
    };

export const useBoardLinkContent = (): UseBoardLinkContentResult => {
    const [searchQuery, setSearchQuery] = useState<string>('');

    const selectBoardLinkContentData = useMemo(() => makeSelectBoardLinkLists(), []);
    const { gridSize, recentViewedItems, allLoadedBoardItems } = useSelector(selectBoardLinkContentData);

    // TODO-INSERTMENU: Using dummy search for now
    const items = useMemo(() => {
        const normalisedQuery = getNormalisedTitle(searchQuery);
        if (normalisedQuery) {
            return allLoadedBoardItems
                .filter((item) => getNormalisedTitle(getTitle(item.board)).includes(normalisedQuery))
                .sort(sortMatchingBoardItems(normalisedQuery))
                .slice(0, SEARCH_SIZE);
        }

        if (!recentViewedItems.length) {
            return allLoadedBoardItems.slice(0, SEARCH_SIZE);
        }

        return recentViewedItems;
    }, [allLoadedBoardItems, recentViewedItems, searchQuery]);

    return {
        searchQuery,
        setSearchQuery,
        gridSize,
        items,
        isLoading: false,
        hasError: false,
        isEmpty: items.length === 0,
    };
};
